import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { STATUS, KANBAN_COLUMNS } from "@/lib/mock";
import TicketCard from "@/components/TicketCard";
import TicketDrawer from "@/components/TicketDrawer";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Kanban board driven by real ticket data.
 * Props:
 *   tickets       — array
 *   onChanged()   — reload callback after any status/assign/close mutation
 *   canAdvance    — role guard (default true for admin/tech)
 */
export default function Kanban({ tickets = [], onChanged, canAdvance = true }) {
  const [dragId, setDragId] = useState(null);
  const [openTicket, setOpenTicket] = useState(null);

  const columns = useMemo(() =>
    KANBAN_COLUMNS.map((k) => ({
      key: k,
      ...STATUS[k],
      items: tickets.filter((t) => t.status === k),
    })), [tickets]);

  const onDrop = async (colKey) => {
    if (!dragId) return;
    const t = tickets.find((x) => x.id === dragId);
    setDragId(null);
    if (!t || t.status === colKey || !canAdvance) return;

    try {
      // Map the drop into the correct worker mutation.
      if (colKey === "assigned") {
        // Cannot assign without a user; nudge to open drawer instead.
        setOpenTicket(t);
        toast.message("Pick an assignee inside the ticket to move to Assigned.");
        return;
      }
      if (colKey === "in_progress") {
        // Assignee acknowledge (or admin sets status)
        try {
          await api.ackTicket(t.id);
        } catch {
          await api.setStatus(t.id, "in_progress");
        }
      } else if (colKey === "resolved") {
        const note = window.prompt("Add a short resolution note:", "Resolved.");
        if (!note) return;
        await api.setStatus(t.id, "resolved", note);
      } else if (colKey === "closed") {
        await api.closeTicket(t.id);
      } else if (colKey === "open") {
        await api.reopenTicket(t.id, "Reopened from Kanban");
      }
      toast.success(`${t.ticket_no} → ${STATUS[colKey].label}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.message || "Could not update ticket");
    }
  };

  return (
    <>
      <div
        className="grid gap-4 overflow-x-auto pb-2"
        style={{ gridTemplateColumns: `repeat(${KANBAN_COLUMNS.length}, minmax(260px, 1fr))` }}
        data-testid="kanban-board"
      >
        {columns.map((col) => (
          <div
            key={col.key}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(col.key)}
            className="min-w-[260px]"
            data-testid={`kanban-col-${col.key}`}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: col.color }} />
                <h3 className="font-display text-lg font-extrabold tracking-tighter">{col.label}</h3>
              </div>
              <span className="text-mono text-[11px] px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/5">
                {col.items.length}
              </span>
            </div>
            <div className="space-y-3 min-h-[80px]">
              <AnimatePresence initial={false}>
                {col.items.map((t) => (
                  <motion.div
                    key={t.id}
                    layout
                    draggable={canAdvance}
                    onDragStart={() => setDragId(t.id)}
                    onDragEnd={() => setDragId(null)}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.2 }}
                  >
                    <TicketCard ticket={t} onOpen={setOpenTicket} />
                  </motion.div>
                ))}
              </AnimatePresence>
              {col.items.length === 0 && (
                <div className="rounded-2xl border border-dashed border-zinc-300 dark:border-zinc-700 h-20 grid place-items-center text-xs text-muted-foreground">
                  {canAdvance ? "Drop here" : "Empty"}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <TicketDrawer
        ticketId={openTicket?.id}
        onClose={() => setOpenTicket(null)}
        onChanged={() => { onChanged?.(); }}
      />
    </>
  );
}
